import { Injectable } from "@angular/core";
import { FillTolerancePromiseService } from "./FillTolerancePromiseService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { FillOrderData } from "../../model/core/FillOrderData";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import {
  IFillToleranceService,
  CHANNEL_FILL_TOLERANCE_SERVICE,
  DetailFillToleranceResponse,
} from "../IFillToleranceService";
import { OrderDataLookupService } from "../../lookup/model/impl/OrderDataLookupService";
import { DealtPricingService } from "../../lookup/pricing/impl/DealtPricingService";
import { TranslateService } from "@ngx-translate/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class FillTolerancePrimaryService
  extends FillTolerancePromiseService
  implements IFillToleranceService
{
  protected broker: ServiceMessageBroker;

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private orderDataLookupService: OrderDataLookupService,
    private pricingService: DealtPricingService,
    private ccyPairLookupService: CcyPairLookupService,
    private translationService: TranslateService,
  ) {
    super();
    this.broker = this.factory.createMessageBroker(CHANNEL_FILL_TOLERANCE_SERVICE, true, true);

    this.broker.registerMethod(
      "checkQuickFillTolerance",
      [PRIMITIVE],
      this.checkQuickFillTolerance.bind(this),
      PRIMITIVE,
    );

    this.broker.registerMethod(
      "checkDetailFillTolerance",
      [PRIMITIVE],
      this.checkDetailFillTolerance.bind(this),
      PRIMITIVE,
    );
  }

  checkQuickFillTolerance(orders: Array<CubiqTS.OrderDataImpl>): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      // implement tolderance check on the orders here
      let failedIds = [];

      orders.forEach((order) => {
        let ccys: Array<string> = [order.baseCode, order.nonBaseCode];
        let pair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(ccys[0], ccys[1]);
        if (pair && pair.fillRateTolerance != null) {
          let marketSpot: CubiqTS.BigNumber = new CubiqTS.BigNumber(
            this.pricingService.getBlotterSpotTracking(order),
          );
          let mktMid: string = this.pricingService.getBlotterMarketMidRate(order);
          let mid: string = mktMid != "-" ? mktMid : "0";
          let marketMid: CubiqTS.BigNumber = new CubiqTS.BigNumber(mid);
          let isInvert: boolean = order.isInvertedOrderFg;
          if (this.recalculateTolerance(pair, marketSpot, marketMid, isInvert)) {
            let tol: string =
              pair.fillRateTolerance.toString() +
              " " +
              this.translationService.instant(
                "UnitType." + CubiqTS.UnitType[pair.fillRateToleranceUnits],
              );
            let error;
            if (mktMid.indexOf("no-rate-strike") > -1) {
              error = this.translationService.instant("tolerance.warning.norate.message", [
                this.formatRate(pair, isInvert, marketSpot),
                this.formatRate(pair, isInvert, marketMid),
                tol,
              ]);
            } else {
              error = this.translationService.instant("tolerance.warning.message", [
                this.formatRate(pair, isInvert, marketSpot),
                this.formatRate(pair, isInvert, marketMid),
                tol,
              ]);
            }
            failedIds.push({
              orderId: order.orderId.toString(),
              orderRef: order.orderRef,
              error: error,
            });
          }
        }
      });
      resolve({ origRequest: orders, failedIds: failedIds });
    });
  }

  private formatRate(
    pair: CubiqTS.CurrencyPairImpl,
    isInvert: boolean,
    rate: CubiqTS.BigNumber,
  ): string {
    let decs = rate.decimalPlaces();
    let minLength = isInvert ? pair.spotInvertDecimalsQty : pair.rateDecimalsQty;
    let maxLength = isInvert
      ? Math.max(pair.spotInvertDecimalsQty, pair.limitEntrySpotInvertDecimals)
      : Math.max(pair.rateDecimalsQty, pair.limitEntrySpotDecimals);
    if (decs > maxLength) {
      return rate.toFixed(maxLength);
    } else if (decs <= minLength) {
      return rate.toFixed(minLength);
    } else {
      return rate.toFixed(decs);
    }
  }

  checkDetailFillTolerance(
    fillorderData: Array<FillOrderData>,
  ): Promise<DetailFillToleranceResponse> {
    return new Promise<any>((resolve, reject) => {
      let failedIds = [];

      fillorderData.forEach((fod) => {
        let pair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(
          fod.baseCcy,
          fod.nonBaseCcy,
        );
        if (pair && pair.fillRateTolerance != null) {
          let marketSpot: CubiqTS.BigNumber = new CubiqTS.BigNumber(fod.mktSpot);
          let order: CubiqTS.OrderDataImpl = this.orderDataLookupService.getOrderData(fod.orderId);
          let mid = this.pricingService.getBlotterMarketMidRate(order);
          if (mid == "-") {
            mid = "0";
          }
          let marketMid: CubiqTS.BigNumber = new CubiqTS.BigNumber(mid);
          if (this.recalculateTolerance(pair, marketSpot, marketMid, fod.isInvert)) {
            let tol: string =
              pair.fillRateTolerance.toString() +
              " " +
              this.translationService.instant(
                "UnitType." + CubiqTS.UnitType[pair.fillRateToleranceUnits],
              );
            let error;
            if (this.pricingService.isSpotRateTradeable(order)) {
              error = this.translationService.instant("tolerance.warning.message", [
                this.formatRate(pair, fod.isInvert, marketSpot),
                this.formatRate(pair, fod.isInvert, marketMid),
                tol,
              ]);
            } else {
              error = this.translationService.instant("tolerance.warning.norate.message", [
                this.formatRate(pair, fod.isInvert, marketSpot),
                this.formatRate(pair, fod.isInvert, marketMid),
                tol,
              ]);
            }
            failedIds.push({
              orderId: fod.orderId,
              orderRef: fod.orderRef,
              error: error,
            });
          }
        }
      });

      resolve({ origRequest: fillorderData, failedIds: failedIds });
    });
  }

  protected recalculateTolerance(
    pair: CubiqTS.CurrencyPairImpl,
    marketSpot: CubiqTS.BigNumber,
    marketMid: CubiqTS.BigNumber,
    isInvert: boolean,
  ): boolean {
    if (
      pair &&
      pair.fillRateToleranceUnits != undefined &&
      !pair.fillRateTolerance.isNaN() &&
      pair.fillRateTolerance.isGreaterThan(0)
    ) {
      let blotterDif: CubiqTS.BigNumber = this.calculateToleranceDif(
        marketMid,
        isInvert ? pair.spotInvertDecimalsQty : pair.rateDecimalsQty,
        pair,
      );

      let blotterTolerance: CubiqTS.BigNumber = marketMid.minus(marketSpot).abs();

      return blotterTolerance.isGreaterThan(blotterDif);
    }
    return false;
  }

  private calculateToleranceDif(
    marketMid: CubiqTS.BigNumber,
    decimals: number,
    pair: CubiqTS.CurrencyPairImpl,
  ): CubiqTS.BigNumber {
    var dif: CubiqTS.BigNumber;
    if (pair && pair.fillRateToleranceUnits != undefined) {
      if (pair.fillRateToleranceUnits == CubiqTS.UnitType.PERCENT) {
        dif = marketMid.times(pair.fillRateTolerance.dividedBy(100));
      } else {
        dif = pair.fillRateTolerance.times(new CubiqTS.BigNumber(10).pow(-decimals));
      }
    }
    return dif;
  }
}
